import isNil from 'lodash-es/isNil';
import {useLocation} from 'react-router-dom';

import {TLinkDto} from 'shared/types/links';
import {useAppSelector} from 'store/config/hooks';
import {selectUserInfo} from 'store/slices/auth-slice';

/**
 * Ключи динамических параметров, которые могут быть заменены
 */
enum EDynamicQueryKeys {
    ACTION_PAGE = 'actionPage',
    USER = 'user',
}

/**
 * Маркер для динамических параметров, которые должны быть заменены
 */
const DYNAMIC_PARAMETER = 'DYNAMIC!';
/**
 * Значение по умолчанию для имени пользователя, если оно не определено
 */
const USERNAME_FALLBACK = 'unknown_user';

interface IUseResolveDynamicParameters {
  parameters: TLinkDto['queryParameters'] | TLinkDto['bodyParameters'];
  url?: TLinkDto['url'];
}

interface IUseResolveDynamicParametersResult {
  /** URL с подставленными параметрами в query-строке */
  urlWithParams: string;
  /** URL для скачивания файла (с fallback на '/' при отсутствии url) */
  downloadFileUrl: string;
  /** Объект с разрешенными параметрами */
  resolvedParameters: TLinkDto['bodyParameters'] | TLinkDto['queryParameters'];
}

/**
 * Хук для подстановки динамических параметров в URL и параметрах запроса
 *
 * @param {string} [params.url] - Базовый URL для формирования конечного адреса
 * @param {TLinkDto['queryParameters'] | TLinkDto['bodyParameters']} params.parameters -
 * Параметры запроса/тела для динамической замены
 *
 * @returns {IUseResolveDynamicParametersResult} Объект с результатами:
 * @returns {string} urlWithParams - URL с подставленными параметрами в query-строке
 * @returns {string} downloadFileUrl - URL для скачивания файла (с fallback на '/' при отсутствии url)
 * @returns {TLinkDto['bodyParameters'] | TLinkDto['queryParameters']} resolvedParameters -
 * Объект с разрешенными параметрами
 *
 * @example
 * Возвращает URL с подставленным именем пользователя
 * const { urlWithParams } = useResolveDynamicParameters({
 *   url: '/api/user',
 *   parameters: { user: 'DYNAMIC!' }
 * });
 *
 * @example
 * Возвращает текущий URL страницы как параметр
 * const { resolvedParameters } = useResolveDynamicParameters({
 *   parameters: { redirectUrl: 'DYNAMIC!' }
 * });
 */
export const useResolveDynamicParameters = ({
    url,
    parameters,
}: IUseResolveDynamicParameters): IUseResolveDynamicParametersResult => {
    const user = useAppSelector(selectUserInfo);
    const currentLocationUrl = useLocation().pathname;

    const resolvedParameters = parameters && Object.entries(parameters).reduce<Record<string, string>>(
        (acc, [key, value]) => {
            if (value !== DYNAMIC_PARAMETER) {
                acc[key] = value;
                return acc;
            }

            switch (key) {
            case EDynamicQueryKeys.ACTION_PAGE:
                acc[key] = currentLocationUrl;
                break;
            case EDynamicQueryKeys.USER:
                acc[key] = user?.userName ?? USERNAME_FALLBACK;
                break;
            default:
                acc[key] = value;
            }

            return acc;
        },
        {},
    );

    const queryParamsString = resolvedParameters
        ? `?${new URLSearchParams(resolvedParameters).toString()}`
        : '';

    const urlWithParams = `${url}${queryParamsString}`;

    const downloadFileUrl = isNil(urlWithParams) ? '/' : urlWithParams;

    return {
        urlWithParams,
        downloadFileUrl,
        resolvedParameters,
    };
};
